import {
  isStorageQuotaError,
  loadGmPartCatalogFromIdb,
  saveGmPartCatalogToIdb,
} from "@/gm/gm-monster-catalog-idb";
import {
  createPartDefinition,
  type HomePartDefinition,
  type HomeRoomData,
} from "@/home/storage";
import {
  kindPreset,
  normalizePartKind,
  type HomePartKind,
} from "@/home/part-kind";

export const GM_PART_CATALOG_VERSION = 1 as const;

/** Scene furniture kinds — monsters live in the monster catalog. */
export const GM_SCENE_PART_KINDS: HomePartKind[] = [
  "solid",
  "prop",
  "attack_dummy",
  "floor",
  "platform",
  "shell",
  "backdrop",
];

export type GmPartTemplate = {
  version: typeof GM_PART_CATALOG_VERSION;
  id: string;
  name: string;
  imageDataUrl: string;
  cellsW: number;
  cellsH: number;
  kind: HomePartKind;
  createdAt: number;
  updatedAt: number;
};

export const GM_PART_PLACEHOLDER_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect fill="#334155" width="64" height="64" rx="10"/><rect x="14" y="20" width="36" height="28" rx="4" fill="#94a3b8"/><text x="32" y="58" text-anchor="middle" fill="#e2e8f0" font-family="system-ui,sans-serif" font-size="9">PART</text></svg>`,
  );

let catalogCache: GmPartTemplate[] | null = null;
let hydratePromise: Promise<void> | null = null;

function newPartId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `gm-part-${crypto.randomUUID()}`;
  }
  return `gm-part-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

export function isGmScenePartKind(kind: HomePartKind): boolean {
  return kind !== "monster" && GM_SCENE_PART_KINDS.includes(kind);
}

export function normalizeGmPartTemplate(raw: unknown): GmPartTemplate | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<GmPartTemplate>;
  if (typeof p.id !== "string" || !p.id || typeof p.name !== "string" || !p.name) return null;
  const kindRaw = normalizePartKind(p.kind);
  const kind = isGmScenePartKind(kindRaw) ? kindRaw : "prop";
  const now = Date.now();
  return {
    version: GM_PART_CATALOG_VERSION,
    id: p.id,
    name: p.name.trim().slice(0, 80),
    imageDataUrl:
      typeof p.imageDataUrl === "string" && p.imageDataUrl.startsWith("data:")
        ? p.imageDataUrl
        : GM_PART_PLACEHOLDER_IMAGE,
    cellsW: clampInt(p.cellsW, 1, 4, 1),
    cellsH: clampInt(p.cellsH, 1, 4, 1),
    kind,
    createdAt: typeof p.createdAt === "number" ? p.createdAt : now,
    updatedAt: typeof p.updatedAt === "number" ? p.updatedAt : now,
  };
}

async function persistCatalog(items: GmPartTemplate[]): Promise<void> {
  await saveGmPartCatalogToIdb(items);
  catalogCache = items;
}

export async function hydrateGmPartCatalog(): Promise<void> {
  if (catalogCache) return;
  if (hydratePromise) {
    await hydratePromise;
    return;
  }
  hydratePromise = (async () => {
    let items = await loadGmPartCatalogFromIdb();
    if (!items?.length) items = [];
    catalogCache = items
      .map((item) => normalizeGmPartTemplate(item))
      .filter((item): item is GmPartTemplate => item !== null);
  })();
  await hydratePromise;
}

export function loadGmPartCatalog(): GmPartTemplate[] {
  return catalogCache ?? [];
}

export async function upsertGmPartCatalog(entry: GmPartTemplate): Promise<void> {
  await hydrateGmPartCatalog();
  const list = [...loadGmPartCatalog()];
  const next = { ...entry, updatedAt: Date.now() };
  const idx = list.findIndex((m) => m.id === next.id);
  if (idx >= 0) list[idx] = next;
  else list.push(next);
  await persistCatalog(list);
}

export async function deleteGmPartFromCatalog(id: string): Promise<void> {
  await hydrateGmPartCatalog();
  await persistCatalog(loadGmPartCatalog().filter((m) => m.id !== id));
}

export function createGmPartTemplate(
  input: Omit<GmPartTemplate, "version" | "id" | "createdAt" | "updatedAt"> & {
    id?: string;
    createdAt?: number;
  },
): GmPartTemplate {
  const now = Date.now();
  const kind = normalizePartKind(input.kind);
  const base = normalizeGmPartTemplate({
    ...input,
    kind: isGmScenePartKind(kind) ? kind : "prop",
    id: input.id ?? newPartId(),
    createdAt: input.createdAt ?? now,
    updatedAt: now,
  });
  if (!base) throw new Error("invalid part template");
  return base;
}

export function gmPartTemplateToPart(template: GmPartTemplate): HomePartDefinition {
  return createPartDefinition({
    id: template.id,
    name: template.name,
    imageDataUrl: template.imageDataUrl,
    cellsW: template.cellsW,
    cellsH: template.cellsH,
    kind: template.kind,
    createdAt: template.createdAt,
  });
}

export function mergeGmPartCatalogIntoHome(home: HomeRoomData): HomeRoomData {
  const templates = loadGmPartCatalog();
  if (templates.length === 0) return home;
  const parts = [...home.parts];
  for (const template of templates) {
    const part = gmPartTemplateToPart(template);
    const idx = parts.findIndex((p) => p.id === part.id);
    if (idx >= 0) parts[idx] = part;
    else parts.push(part);
  }
  return { ...home, parts };
}

export function defaultNewPartForm(): Omit<
  GmPartTemplate,
  "version" | "id" | "createdAt" | "updatedAt"
> {
  return {
    name: "ชิ้นใหม่",
    imageDataUrl: GM_PART_PLACEHOLDER_IMAGE,
    cellsW: 1,
    cellsH: 1,
    kind: "prop",
  };
}

export function gmSceneKindLabel(kind: HomePartKind): string {
  return kindPreset(kind).tabLabelTh;
}

export { isStorageQuotaError };
